import React, { useEffect, useState } from "react";
import { Link, NavigateFunction, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { httpGet } from "../http";
import {
  CalendarIcon,
  ChevronRightIcon,
  PlusIcon,
  MapPinIcon,
  ShoppingBagIcon,
  ClockIcon,
} from "lucide-react";
import { PurchaseType } from "./Show";
import { MoneyField } from "../helpers/br_number_field";
import { useToast } from "../components/Toast";

type PaginationType = {
  total: {
    loaded: number;
    created: number;
  };
  nextCursor: string | null;
};

type StateType = {
  loaded: boolean;
  list: PurchaseType[];
  pagination: PaginationType | null;
  pendingSectionExpanded: boolean;
  loadingMore: boolean;
};

type LineLinkPropsType = {
  to: string;
  disabled: boolean;
  children: any;
};

type TableHeaderPropsType = {
  status: string;
  pagination: PaginationType | null;
  listLength: number;
};

const TableHeader = ({
  status,
  pagination,
  listLength,
}: TableHeaderPropsType) => {
  const totalItems =
    (status === "created"
      ? pagination?.total.created
      : pagination?.total.loaded) || 0;
  const bgColor =
    status === "created"
      ? "bg-yellow-50 border-yellow-200"
      : "bg-green-50 border-green-200";
  const iconColor = status === "created" ? "text-yellow-600" : "text-green-600";
  const iconBgColor = status === "created" ? "bg-yellow-100" : "bg-green-100";
  const labelColor =
    status === "created" ? "text-yellow-700" : "text-green-700";

  return (
    <div
      className={`flex items-center justify-between p-4 border-b ${bgColor}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          <ShoppingBagIcon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {status === "created"
              ? "Compras Pendentes"
              : "Histórico de Compras"}
          </h3>
          <p className={`text-sm ${labelColor}`}>
            Mostrando{" "}
            {totalItems > listLength
              ? `${listLength} de ${totalItems} compras`
              : `${listLength} ${listLength === 1 ? "compra" : "compras"}`}
          </p>
        </div>
      </div>
    </div>
  );
};

const LineLink = (props: LineLinkPropsType) => {
  if (props.disabled) {
    return <>{props.children}</>;
  } else {
    return (
      <Link to={props.to} className="block">
        {props.children}
      </Link>
    );
  }
};

const loadPurchases = async (
  setPurchases: Function,
  navigate: NavigateFunction,
  status: string,
  nextCursor: string,
  showError: (message: string) => void,
) => {
  try {
    const result = await httpGet(
      `/api/v1/purchases?status=${status}&cursor=${nextCursor || ""}`,
    );

    if (result.error === "logged_out") {
      navigate("/dash/logout");
    } else if (result.error === "offline") {
      setPurchases(result.body.data, result.body.pagination);
    } else if (result.error) {
      showError("Falha ao listar compras");
    } else if (result.body) {
      setPurchases(result.body.data, result.body.pagination);
    }
  } finally {
  }
};

type PropsType = {
  status: "created" | "loaded";
};

export const PurchaseList = (props: PropsType) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [state, setState] = useState<StateType>({
    loaded: false,
    list: [],
    pendingSectionExpanded: false,
    pagination: null,
    loadingMore: false,
  });

  useEffect(() => {
    setState({
      loaded: false,
      list: [],
      pendingSectionExpanded: false,
      pagination: null,
      loadingMore: false,
    });
  }, [props.status]);

  useEffect(() => {
    if (!state.loaded) {
      const setPurchases = (
        purchases: PurchaseType[],
        pagination: PaginationType,
      ) => {
        setState((prevState) => ({
          ...prevState,
          loaded: true,
          list: purchases,
          pagination,
        }));
      };

      loadPurchases(setPurchases, navigate, props.status, "", showError);
    }
  }, [state.loaded, props.status, navigate, showError]);

  const handleLoadMore = async () => {
    if (!state.pagination?.nextCursor || state.loadingMore) return;

    setState({ ...state, loadingMore: true });

    const appendPurchases = (
      newPurchases: PurchaseType[],
      pagination: PaginationType,
    ) => {
      setState((prevState) => ({
        ...prevState,
        list: [...prevState.list, ...newPurchases],
        pagination,
        loadingMore: false,
      }));
    };

    await loadPurchases(
      appendPurchases,
      navigate,
      props.status,
      state.pagination.nextCursor,
      showError,
    );
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-24">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Minhas Compras</h1>
          <p className="text-gray-600 mt-1">
            Histórico de todas as suas compras no mercado
          </p>
        </div>
        <Link
          to="/dash/purchases/new"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
        >
          <ShoppingBagIcon className="h-5 w-5" />
          <span>Registrar Compra</span>
        </Link>
      </div>

      <div className="space-y-6 relative">
        {!state.loaded && (
          <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando compras...</p>
            </div>
          </div>
        )}
        {state.list.length === 0 && state.loaded ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="text-center py-12">
              <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma compra encontrada
              </h3>
              <p className="text-gray-600 mb-6">
                Comece registrando sua primeira compra
              </p>
              <Link
                to="/dash/purchases/new"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Registrar Primeira Compra</span>
              </Link>
            </div>
          </div>
        ) : null}

        {props.status !== "created" &&
          (state.pagination?.total.created || 0) > 0 && (
            <div className="bg-gray-50 rounded-lg border border-gray-100">
              <Link to="/dash/purchases/pending" className="block">
                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {state.pagination?.total.created} compras sendo
                        processadas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </Link>
            </div>
          )}

        {props.status === "created" && (
          <div className="bg-gray-50 rounded-lg border border-gray-100">
            <Link to="/dash/purchases" className="block">
              <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2">
                  <ShoppingBagIcon className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Ir para a lista de compras processadas
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </Link>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <TableHeader
            pagination={state.pagination}
            status={props.status}
            listLength={state.list.length}
          />

          <div className="divide-y divide-gray-100">
            {state.list.map((p) => {
              const pm =
                props.status === "loaded"
                  ? p.metadata || {}
                  : { date: p.createdAt };
              const purchaseDateTime = pm.date
                ? DateTime.fromISO(pm.date)
                : null;

              return (
                <div
                  key={p.id}
                  className="hover:bg-gray-50/50 cursor-pointer transition-colors duration-200"
                >
                  <LineLink to={`/dash/purchases/${p.id}`} disabled={false}>
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <MapPinIcon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 truncate">
                                {pm.storeName || "Loja não identificada"}
                              </h3>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-600">
                            {purchaseDateTime && (
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-gray-400" />
                                <span>
                                  {purchaseDateTime.toFormat("dd/MM/yyyy")}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span>
                                  {purchaseDateTime.toFormat("HH:mm")}
                                </span>
                              </div>
                            )}
                            {pm.total && (
                              <div className="flex items-center gap-2 min-w-20 ml-4">
                                <span className="font-semibold text-green-600">
                                  <MoneyField value={pm.total} />
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="ml-6">
                          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200">
                            <ChevronRightIcon className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </LineLink>
                </div>
              );
            })}
          </div>

          {state.pagination?.nextCursor && (
            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleLoadMore}
                disabled={state.loadingMore}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Carregando...</span>
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4" />
                    <span>Carregar Mais</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
